import moment from 'moment';

export const MINUTE_IN_SECONDS = 60;
export const HOUR_IN_SECONDS = 3600;
export const DAY_IN_SECONDS = 86400;

export default class StatsLogic {
  /**
   *
   * @param Array data
   * @param Date split - Which date splits the current and previous period
   * @param String prop - Which property of the object should be compared
   *
   * @return Object - of arrays containing the current and previous items
   *
   */
  static splitIntoCurrentAndPreviousPeriods(data, split, prop = 'createdAt') {
    let previous = [];
    let current = [];

    split = moment(split).startOf('day');

    for (var i = data.length - 1; i >= 0; i--) {
      if (moment(data[i][prop]) >= split) {
        current.push(data[i]);
      } else {
        previous.push(data[i]);
      }
    }

    return {
      current: current,
      previous: previous,
    };
  }

  /**
   * Get an array of every day between a start and end date
   *
   * @param Date startDate
   * @param Date endDate
   * @param String format - Change how the outputed days are formated
   *
   * @return Array - of every date between the start and end day
   *
   */
  static enumerateDaysBetweenDates(startDate, endDate, format) {
    var dates = [];

    var currDate = moment(startDate).startOf('day');
    var lastDate = moment(endDate).startOf('day');

    // Need to add the start date otherwise it gets missed
    dates.push(currDate.format(format));

    while (currDate.add(1, 'days').diff(lastDate) <= 0) {
      dates.push(currDate.clone().format(format));
    }

    return dates;
  }

  /**
   * Calculate how many days the period spans then subtract that from the start date to get the previous period
   *
   * @param Date startDate
   * @param Date endDate
   * @return Date - The start date of the previous period
   *
   */
  static getPreviousPeriod(startDate, endDate, format) {
    if (!startDate instanceof Date || !endDate instanceof Date) {
      return null;
    }

    let end = moment(endDate).startOf('day');
    let start = moment(startDate).startOf('day');

    let diff = StatsLogic.getDaysBetweenPeriod(start, end, format);

    return start.subtract(diff, 'days');
  }

  /**
   * Calculate how many days the period spans
   *
   * @param Date startDate
   * @param Date endDate
   * @return int - The number of days between the period
   *
   */
  static getDaysBetweenPeriod(startDate, endDate) {
    if (!startDate instanceof Date || !endDate instanceof Date) {
      return null;
    }

    let momentEndDate = moment(endDate);
    let momentStartDate = moment(startDate);

    return momentEndDate.diff(momentStartDate, 'days');
  }

  /**
   * Get the median value from an array
   *
   * @param Array values - array of values to get the median
   * @return int
   *
   */
  static median(values = []) {
    // IMPROVE:: this should perhaps be null and let the calling function handle it
    if (!Array.isArray(values) || values.length === 0) {
      return 0;
    }

    // If there is only 1 value then this is our median
    if (values.length == 1) {
      return values[0];
    }

    values.sort(function (a, b) {
      return a - b;
    });

    var half = Math.floor(values.length / 2);

    if (values.length % 2) {
      return values[half];
    } else {
      return (values[half - 1] + values[half]) / 2.0;
    }
  }

  /**
   * Convert seconds to human readable value
   *
   * @param int seconds
   * @return object - with value and unit i.e {value: 1, unit: 'days'}
   *
   */
  static secondsToHumanValue(seconds) {
    if (isNaN(seconds) || !isFinite(seconds)) {
      let type = typeof seconds;
      console.warn(
        'Non integer passed to secondsToHumanValue(). typeof: (' +
          type +
          ') passed'
      );
      return { value: seconds, unit: 'seconds' };
    }

    let unit = StatsLogic.valueBetterAs(seconds);
    let humanValue = StatsLogic.secondsTo(seconds, unit);

    return { value: humanValue, unit: unit };
  }

  /**
   * Determines the best - most readable - unit to display seconds in
   *
   * @param int seconds
   * @return string - a unit of time
   *
   */
  static valueBetterAs(seconds) {
    if (seconds >= HOUR_IN_SECONDS) {
      return 'hours';
    }

    if (seconds >= MINUTE_IN_SECONDS) {
      return 'minutes';
    }

    return 'seconds';
  }

  /**
   * Convert seconds to another unit of time
   *
   * @param int seconds
   * @param string unit - desired conversion
   * @return number
   *
   */
  static secondsTo(seconds, unit) {
    switch (unit) {
      case 'hours':
        return seconds / HOUR_IN_SECONDS;
      case 'minutes':
        return seconds / MINUTE_IN_SECONDS;

      default:
        return seconds;
    }
  }

  /**
   * Convert Object to GoogleCharts bar chart array
   *
   * @param array enumeration
   * @param object data
   * @return array
   *
   */
  static toBarChartArray(enumeration = [], data = {}) {
    return enumeration.map((thisOne, i) => {
      return [thisOne].concat(data[enumeration[i]]);
    });
  }

  /**
   * Count occurrences of items in an array
   *
   * @param array data
   * @return object - containing the item as a key and the count as a value
   *
   */
  static countOccurrences(data = []) {
    return data.reduce(function (acc, curr) {
      if (typeof acc[curr] == 'undefined') {
        acc[curr] = 1;
      } else {
        acc[curr] += 1;
      }

      return acc;
    }, {});
  }

  /**
   * Convert chart data in seconds to an easier to read unit
   *
   * @param object data
   * @return array - [the data after formatting, the unit it has been converted to]
   *
   */
  static getHumanReadableSeconds(data) {
    let keys = Object.keys(data);
    let values = Object.values(data);

    let max = Math.max(...values);

    let unit = StatsLogic.valueBetterAs(max);

    let formatted = {};

    for (var i = keys.length - 1; i >= 0; i--) {
      formatted[keys[i]] = StatsLogic.secondsTo(data[keys[i]], unit);
    }

    return [formatted, unit];
  }

  static getHumanReadableSecondsString(time) {
    let res = [];
    let remainder = parseInt(time, 10);

    const hours = Math.floor(remainder / 3600);
    remainder = remainder % 3600;

    if (hours > 0) {
      res.push(`${hours}h`);
    }

    const minutes = Math.floor(remainder / 60);
    remainder = remainder % 60;

    if (minutes > 0) {
      res.push(`${minutes}m`);
    }

    const seconds = Math.floor(remainder);

    if (seconds > 0) {
      res.push(`${seconds}s`);
    }

    return res.join(', ');
  }

  static getHumanReadableTooltipString(time) {
    let res = [];
    let remainder = parseInt(time, 10);

    const hours = Math.floor(remainder / 3600);
    remainder = remainder % 3600;

    if (hours > 0) {
      res.push(`${hours}h`);
    }

    const minutes = Math.floor(remainder / 60);
    remainder = remainder % 60;

    if (minutes > 0) {
      res.push(`${minutes}m`);
    }

    const seconds = Math.floor(remainder);

    if (seconds > 0 && hours == 0) {
      res.push(`${seconds}s`);
    }

    return res.join(' ');
  }

  static isFilteredConversation(agentsFilter = [], tagsFilter = []) {
    return (conversation) => {
      const hasAgent = agentsFilter.length
        ? !!conversation.all_assigned_agent_ids.find((id) =>
            agentsFilter.includes(id)
          )
        : true;
      const hasTag = tagsFilter.length
        ? !!conversation.Tags.map((tag) => tag.id).find((id) =>
            tagsFilter.includes(id)
          )
        : true;
      if (hasAgent && hasTag) {
        return true;
      }
      return false;
    };
  }

  static splitPeriodIntoRanges(allPeriodDays) {
    const _allPeriodDays = [].concat(allPeriodDays);
    const daysPerPeriod = Math.ceil(allPeriodDays.length / 15);
    let newAllPeriodDays = [];
    while (_allPeriodDays.length) {
      const periodDays = _allPeriodDays.splice(0, daysPerPeriod);
      newAllPeriodDays.push(periodDays);
    }
    return newAllPeriodDays;
  }

  static aggregateConversationsByRanges(
    conversationsByDay,
    dayRanges,
    aggregateCallback
  ) {
    return dayRanges.map((dayRange) => {
      const conversationsInRange = dayRange.reduce((conversations, day) => {
        const conversationsOnDay = conversationsByDay[day] || [];
        return conversations.concat(conversationsOnDay);
      }, []);
      const label =
        dayRange.length > 1
          ? `${moment(dayRange[0]).format('MMM D')} - ${moment(
              dayRange[dayRange.length - 1]
            ).format('MMM D')}`
          : moment(dayRange[0]).format('MMM D');
      const data = aggregateCallback(conversationsInRange);
      if (Array.isArray(data)) {
        return [label, ...data];
      }
      return [label, data];
    });
  }

  static aggregateStatsByRangeAsHour(
    statsByDay,
    dayRange,
    aggregateCallback,
    getStatDate
  ) {
    const day = dayRange[0];
    const dayDate = this.dayToDate(day);
    const hours = new Array(24)
      .fill(null)
      .map(
        (_, index) =>
          new Date(
            dayDate.getFullYear(),
            dayDate.getMonth(),
            dayDate.getDate(),
            index
          )
      );
    const blankByHour = hours.reduce((byHour, hour) => {
      return { ...byHour, [hour]: [] };
    }, {});
    const statsByHour = (statsByDay[day] || []).reduce((byHour, stat) => {
      const statDate = new Date(getStatDate(stat));
      const startOfHour = new Date(
        statDate.getFullYear(),
        statDate.getMonth(),
        statDate.getDate(),
        statDate.getHours()
      );
      return { ...byHour, [startOfHour]: byHour[startOfHour].concat(stat) };
    }, blankByHour);
    return Object.entries(statsByHour).map(([hour, stats]) => {
      const label = moment(hour).format('hh a');
      const data = aggregateCallback(stats);
      if (Array.isArray(data)) {
        return [label, ...data];
      }
      return [label, data];
    });
  }

  static aggregateStatsByRangeAsDay(statsByDay, dayRange, aggregateCallback) {
    return dayRange.map((day) => {
      const statsOnDay = statsByDay[day] || [];
      const label = moment(day).format('MMM D');
      const data = aggregateCallback(statsOnDay);
      if (Array.isArray(data)) {
        return [label, ...data];
      }
      return [label, data];
    });
  }

  static aggregateStatsByRangeAsWeek(statsByDay, dayRange, aggregateCallback) {
    const FIRST_DAY_OF_WEEK = 1; // Monday
    const splitByWeeks = dayRange.reduce((byWeeks, day) => {
      const dayDate = this.dayToDate(day);
      const firstOfWeek = new Date(
        dayDate.getFullYear(),
        dayDate.getMonth(),
        dayDate.getDate() - dayDate.getDay() + FIRST_DAY_OF_WEEK
      );
      const currentDaysInWeek = byWeeks[firstOfWeek] || [];
      return { ...byWeeks, [firstOfWeek]: currentDaysInWeek.concat(day) };
    }, {});
    return Object.entries(splitByWeeks).map(([firstOfWeek, days], index) => {
      const firstOfWeekDate = new Date(firstOfWeek);
      const labelFrom =
        index === 0 ? this.dayToDate(dayRange[0]) : firstOfWeekDate;
      const labelTo =
        index === Object.keys(splitByWeeks).length - 1
          ? this.dayToDate(dayRange[dayRange.length - 1])
          : // Adjusted labelTo to be end of Week.
            new Date(
              firstOfWeekDate.getFullYear(),
              firstOfWeekDate.getMonth(),
              firstOfWeekDate.getDate() + 6
            );
      const label = `${moment(labelFrom).format('MMM D')} - ${moment(
        labelTo
      ).format('MMM D')}`;
      const statsInWeek = days.reduce((stats, day) => {
        const statsOnDay = statsByDay[day] || [];
        return stats.concat(statsOnDay);
      }, []);
      const data = aggregateCallback(statsInWeek);
      if (Array.isArray(data)) {
        return [label, ...data];
      }
      return [label, data];
    });
  }

  static aggregateStatsByRangeAsMonth(statsByDay, dayRange, aggregateCallback) {
    const firstDay = this.dayToDate(dayRange[0]);
    const lastDay = this.dayToDate(dayRange[dayRange.length - 1]);
    const includeYears = firstDay.getFullYear() !== lastDay.getFullYear();

    const getFirstDayOfMonth = (day) => {
      const dayDate = this.dayToDate(day);
      return new Date(dayDate.getFullYear(), dayDate.getMonth(), 1);
    };
    const getLastDayOfMonth = (day) => {
      const dayDate = this.dayToDate(day);
      return new Date(dayDate.getFullYear(), dayDate.getMonth() + 1, 0);
    };
    const daysEqual = (day1, day2) =>
      new Date(day1).getTime() === new Date(day2).getTime();
    const getLabel = (firstOfMonth) => {
      const firstDayOfMonthDate = new Date(firstOfMonth);
      if (includeYears) {
        return moment(firstDayOfMonthDate).format('MMM ’YY');
      }
      return moment(firstDayOfMonthDate).format('MMMM');
    };
    const getFirstLabel = (firstOfMonth) => {
      // If the first of the month is the first day in the range just return the month label
      if (daysEqual(firstDay, firstOfMonth)) {
        return getLabel(firstOfMonth);
      }
      const lastDayOfMonthDate = getLastDayOfMonth(firstOfMonth);
      const day1String = moment(firstDay).format('D');
      const day2String = moment(lastDayOfMonthDate).format('D');
      const monthString = moment(lastDayOfMonthDate).format('MMM');
      if (includeYears) {
        const yearString = moment(lastDayOfMonthDate).format('YY');
        return `${day1String}-${day2String} ${monthString} ’${yearString}`;
      }
      return `${day1String}-${day2String} ${monthString}`;
    };
    const getLastLabel = (firstOfMonth) => {
      const lastDayOfMonthDate = getLastDayOfMonth(firstOfMonth);
      // If the last day of the month is the last day in the range just return the month label
      if (daysEqual(lastDay, lastDayOfMonthDate)) {
        return getLabel(firstOfMonth);
      }
      const firstDayOfMonthDate = new Date(firstOfMonth);
      const day1String = moment(firstDayOfMonthDate).format('D');
      const day2String = moment(lastDay).format('D');
      const monthString = moment(firstDayOfMonthDate).format('MMM');
      if (includeYears) {
        const yearString = moment(firstDayOfMonthDate).format('YYYY');
        return `${day1String}-${day2String} ${monthString} ${yearString}`;
      }
      return `${day1String}-${day2String} ${monthString}`;
    };
    // Group the days by month
    const splitByMonths = dayRange.reduce((byMonths, day) => {
      const firstOfMonth = getFirstDayOfMonth(day);
      const currentDaysInMonth = byMonths[firstOfMonth] || [];
      return { ...byMonths, [firstOfMonth]: currentDaysInMonth.concat(day) };
    }, {});
    // Aggregate stats using the month groups
    return Object.entries(splitByMonths).map(([firstOfMonth, days], index) => {
      let label = getLabel(firstOfMonth);
      // The first and last group should be labeled differently
      if (index === 0) {
        label = getFirstLabel(firstOfMonth);
      }
      if (index === Object.keys(splitByMonths).length - 1) {
        label = getLastLabel(firstOfMonth);
      }

      const statsInMonth = days.reduce((stats, day) => {
        const statsOnDay = statsByDay[day] || [];
        return stats.concat(statsOnDay);
      }, []);
      const data = aggregateCallback(statsInMonth);
      if (Array.isArray(data)) {
        return [label, ...data];
      }
      return [label, data];
    });
  }

  static getConversationCreatedAt(conversation) {
    return conversation.createdAt;
  }

  static getConversationClosedAt(conversation) {
    return conversation.closedAt;
  }

  static GROUPING_TYPE_HOURS = 'hours';
  static GROUPING_TYPE_DAYS = 'days';
  static GROUPING_TYPE_WEEKS = 'weeks';
  static GROUPING_TYPE_MONTHS = 'months';

  static getDayRangeGroupingType(dayRange) {
    if (dayRange.length === 1) {
      return this.GROUPING_TYPE_HOURS;
    }
    if (dayRange.length <= 14) {
      return this.GROUPING_TYPE_DAYS;
    }
    if (dayRange.length <= 90) {
      return this.GROUPING_TYPE_WEEKS;
    }
    return this.GROUPING_TYPE_MONTHS;
  }

  static dayToDate(day) {
    return moment(day).utc().toDate();
  }

  static aggregateStatsByRange(
    statsByDay,
    dayRange,
    aggregateCallback,
    getStatDate
  ) {
    const groupingType = this.getDayRangeGroupingType(dayRange);
    switch (groupingType) {
      case this.GROUPING_TYPE_HOURS:
        return this.aggregateStatsByRangeAsHour(
          statsByDay,
          dayRange,
          aggregateCallback,
          getStatDate
        );
      case this.GROUPING_TYPE_DAYS:
        return this.aggregateStatsByRangeAsDay(
          statsByDay,
          dayRange,
          aggregateCallback,
          getStatDate
        );
      case this.GROUPING_TYPE_WEEKS:
        return this.aggregateStatsByRangeAsWeek(
          statsByDay,
          dayRange,
          aggregateCallback,
          getStatDate
        );
      case this.GROUPING_TYPE_MONTHS:
        return this.aggregateStatsByRangeAsMonth(
          statsByDay,
          dayRange,
          aggregateCallback,
          getStatDate
        );
      default:
        throw new Error(`Unknown grouping type ${groupingType}`);
    }
  }

  static getSlantedTextAngleForGroupingType(groupingType) {
    switch (groupingType) {
      case this.GROUPING_TYPE_HOURS:
        return 45;
      case this.GROUPING_TYPE_WEEKS:
        return 35;
      default:
        return 40;
    }
  }

  static getSlantedTextAngleForRange(dayRange) {
    return this.getSlantedTextAngleForGroupingType(
      this.getDayRangeGroupingType(dayRange)
    );
  }

  static getRatio(arr) {
    const total = arr.reduce((acc, value) => acc + value);
    const result = arr.reduce((x) => (x / total) * 100);
    return result || 0;
  }

  // https://stackoverflow.com/a/18358056/5567905
  static roundTo2(number) {
    return +(Math.round(number + 'e+2') + 'e-2');
  }

  static formatConversationStats(statsByInterval, startDate, endDate) {
    const dateDifference = moment(endDate).diff(startDate, 'hours');
    if (dateDifference < 24) {
      return this.formatConversationStatsIntoHours(statsByInterval);
    }
    if (dateDifference < 24 * 14) {
      return this.formatConversationStatsIntoDays(statsByInterval);
    }
    if (dateDifference < 24 * 90) {
      return this.formatConversationStatsIntoWeeks(statsByInterval, endDate);
    }
    return this.formatConversationStatsIntoMonths(statsByInterval);
  }

  static formatConversationsStartedOnSunday(statsByWeek) {
    const FIRST_DAY_OF_WEEK = 1; // Monday
    return statsByWeek.reduce((stats, { day, value, value2 }, index) => {
      if (index === statsByWeek.length - 1) {
        return stats;
      }

      const dayDate = this.dayToDate(day);
      // Logic needs broken down into if it starts sunday.

      // First Of Week Calculation
      let firstOfWeek = '';
      if (index === 0) {
        firstOfWeek = new Date(
          dayDate.getFullYear(),
          dayDate.getMonth(),
          dayDate.getDate() - dayDate.getDay()
        );
      } else if (index !== 0 && dayDate.getDay() === 0) {
        firstOfWeek = new Date(
          dayDate.getFullYear(),
          dayDate.getMonth(),
          dayDate.getDate() - 7 + FIRST_DAY_OF_WEEK
        );
      } else {
        firstOfWeek = new Date(
          dayDate.getFullYear(),
          dayDate.getMonth(),
          dayDate.getDate() - dayDate.getDay() + FIRST_DAY_OF_WEEK
        );
      }

      // LabelFrom Calculation
      let labelFrom =
        index === 0 ? this.dayToDate(statsByWeek[0].day) : firstOfWeek;

      // LabelTo Calculation
      let labelTo = '';
      if (index === 0) {
        labelTo = labelFrom;
      } else if (index === statsByWeek.length - 2) {
        labelTo = this.dayToDate(statsByWeek[statsByWeek.length - 1].day);
      } else {
        labelTo = new Date(
          firstOfWeek.getFullYear(),
          firstOfWeek.getMonth(),
          firstOfWeek.getDate() + 6
        );
      }

      const label = `${moment(labelFrom).format('MMM D')} - ${moment(
        labelTo
      ).format('MMM D')}`;
      return [
        ...stats,
        { label, value: Number(value), value2: value2 && Number(value2) },
      ];
    }, []);
  }

  static formatConversationStatsIntoWeeks(statsByWeek) {
    const FIRST_DAY_OF_WEEK = 1; // Monday
    const startDay = this.dayToDate(statsByWeek[0].day);
    const startDayIsSunday = startDay.getDay() === 0;

    if (startDayIsSunday) {
      return this.formatConversationsStartedOnSunday(statsByWeek);
    }

    return statsByWeek.reduce((stats, { day, value, value2 }, index) => {
      const firstDay = this.dayToDate(statsByWeek[0].day);
      if (index === statsByWeek.length - 1) {
        return stats;
      }
      const dayDate = this.dayToDate(day);

      const secondWeek = new Date(
        firstDay.getFullYear(),
        firstDay.getMonth(),
        firstDay.getDate() - firstDay.getDay() + FIRST_DAY_OF_WEEK + 7
      );

      let firstOfWeek = new Date(
        dayDate.getFullYear(),
        dayDate.getMonth(),
        dayDate.getDate() - dayDate.getDay() + FIRST_DAY_OF_WEEK
      );

      if (index !== 0 && dayDate.getDay() === 0) {
        firstOfWeek = new Date(
          dayDate.getFullYear(),
          dayDate.getMonth(),
          dayDate.getDate() - 7 + FIRST_DAY_OF_WEEK
        );
      }

      let labelFrom =
        index === 0 ? this.dayToDate(statsByWeek[0].day) : firstOfWeek;

      if (firstOfWeek < secondWeek) {
        labelFrom = this.dayToDate(statsByWeek[0].day);
      }

      const endOfWeek = new Date(
        firstOfWeek.getFullYear(),
        firstOfWeek.getMonth(),
        firstOfWeek.getDate() + 6
      );
      let labelTo = '';
      if (endOfWeek > this.dayToDate(statsByWeek[statsByWeek.length - 1].day)) {
        labelTo = this.dayToDate(statsByWeek[statsByWeek.length - 1].day);
      } else {
        labelTo =
          index === statsByWeek.length - 2
            ? this.dayToDate(statsByWeek[statsByWeek.length - 1].day)
            : new Date(
                firstOfWeek.getFullYear(),
                firstOfWeek.getMonth(),
                firstOfWeek.getDate() + 6
              );
      }
      const label = `${moment(labelFrom).format('MMM D')} - ${moment(
        labelTo
      ).format('MMM D')}`;
      return [
        ...stats,
        { label, value: Number(value), value2: value2 && Number(value2) },
      ];
    }, []);
  }

  static formatConversationStatsIntoMonths(statsByMonth) {
    const firstDay = this.dayToDate(statsByMonth[0].day);
    const lastDay = this.dayToDate(statsByMonth[statsByMonth.length - 1].day);
    const includeYears = firstDay.getFullYear() !== lastDay.getFullYear();

    const getLabel = (firstOfMonth) => {
      const firstDayOfMonthDate = new Date(firstOfMonth);
      if (includeYears) {
        return moment(firstDayOfMonthDate).format('MMM ’YY');
      }
      return moment(firstDayOfMonthDate).format('MMMM');
    };

    const daysEqual = (day1, day2) => {
      const day1Date = new Date(day1);
      const day2Date = new Date(day2);
      return (
        day1Date.getFullYear() === day2Date.getFullYear() &&
        day1Date.getMonth() === day2Date.getMonth() &&
        day1Date.getDate() === day2Date.getDate()
      );
    };

    const getFirstDayOfMonth = (day) => {
      const dayDate = this.dayToDate(day);
      return new Date(dayDate.getFullYear(), dayDate.getMonth(), 1);
    };

    const getLastDayOfMonth = (day) => {
      const dayDate = this.dayToDate(day);
      return new Date(dayDate.getFullYear(), dayDate.getMonth() + 1, 0);
    };

    const getFirstLabel = (firstOfMonth) => {
      // If the first of the month is the first day in the range just return the month label
      if (daysEqual(firstDay, firstOfMonth)) {
        return getLabel(firstOfMonth);
      }
      const lastDayOfMonthDate = getLastDayOfMonth(firstOfMonth);
      const day1String = moment(firstDay).format('D');
      const day2String = moment(lastDayOfMonthDate).format('D');
      const monthString = moment(lastDayOfMonthDate).format('MMM');
      if (includeYears) {
        const yearString = moment(lastDayOfMonthDate).format('YY');
        return `${day1String}-${day2String} ${monthString} ’${yearString}`;
      }
      return `${day1String}-${day2String} ${monthString}`;
    };

    const getLastLabel = (month) => {
      const lastDayOfMonthDate = getLastDayOfMonth(month);
      // If the last day of the month is the last day in the range just return the month label
      if (daysEqual(lastDay, lastDayOfMonthDate)) {
        return getLabel(getFirstDayOfMonth(month));
      }
      const monthDate = new Date(month);
      const day1String = moment(monthDate).format('D');
      const day2String = moment(lastDay).format('D');
      const monthString = moment(monthDate).format('MMM');
      if (includeYears) {
        const yearString = moment(monthDate).format('YYYY');
        return `${day1String}-${day2String} ${monthString} ${yearString}`;
      }
      return `${day1String}-${day2String} ${monthString}`;
    };

    return statsByMonth.reduce((stats, { day, value, value2 }, index) => {
      const firstOfMonth = getFirstDayOfMonth(day);
      let label = getLabel(firstOfMonth);
      if (index === 0) {
        label = getFirstLabel(firstOfMonth);
      }
      if (index === statsByMonth.length - 2) {
        label = getLastLabel(this.dayToDate(day));
      }
      if (index === statsByMonth.length - 1) {
        return stats;
      }
      return [
        ...stats,
        { label, value: Number(value), value2: value2 && Number(value2) },
      ];
    }, []);
  }

  static formatConversationStatsIntoHours(statsByHour) {
    return statsByHour.reduce((stats, { day, value, value2 }, index) => {
      if (index === statsByHour.length - 1) {
        return stats;
      }
      return [
        ...stats,
        {
          label: moment(day).format('hh a'),
          value: Number(value),
          value2: value2 && Number(value2),
        },
      ];
    }, []);
  }

  static formatConversationStatsIntoDays(statsByDay) {
    return statsByDay.reduce((stats, { day, value, value2 }, index) => {
      if (index === statsByDay.length - 1) {
        return stats;
      }
      return [
        ...stats,
        {
          label: moment(day).format('MMM D'),
          value: Number(value),
          value2: value2 && Number(value2),
        },
      ];
    }, []);
  }

  static splitPeriodIntoMonths(startDate, endDate) {
    const recursiveSplit = (startDate, endDate, months = []) => {
      const firstOfNextMonth = moment(startDate)
        .add('1', 'month')
        .add('1', 'day')
        .startOf('month')
        .toDate();
      if (firstOfNextMonth >= endDate) {
        return [...months, moment(endDate).toDate()];
      }
      return recursiveSplit(firstOfNextMonth, endDate, [
        ...months,
        firstOfNextMonth,
      ]);
    };
    return recursiveSplit(startDate, endDate, [moment(startDate).toDate()]);
  }

  static splitPeriodIntoWeeks(startDate, endDate) {
    const FIRST_DAY_OF_WEEK = 1; // Monday
    const start = this.dayToDate(startDate);
    const startIsSunday = start.getDay() === 0;

    const recursiveSplit = (
      startDate,
      endDate,
      weeks = [],
      startIsSunday = false
    ) => {
      const firstOfNextWeek = startIsSunday
        ? moment(startDate).add('1', 'day').startOf('day').toDate()
        : moment(startDate)
            .add('1', 'week')
            .startOf('week')
            .add(FIRST_DAY_OF_WEEK, 'day')
            .toDate();

      if (firstOfNextWeek >= endDate) {
        return [...weeks, moment(endDate).toDate()];
      }
      return recursiveSplit(firstOfNextWeek, endDate, [
        ...weeks,
        firstOfNextWeek,
      ]);
    };
    return recursiveSplit(
      startDate,
      endDate,
      [moment(startDate).toDate()],
      startIsSunday
    );
  }

  static splitPeriodIntoDays(startDate, endDate) {
    const recursiveSplit = (startDate, endDate, days = []) => {
      const startOfNextDay = moment(startDate)
        .add('1', 'day')
        .startOf('day')
        .toDate();
      if (startOfNextDay > moment(endDate).startOf('day')) {
        return [...days, moment(endDate).toDate()];
      }
      return recursiveSplit(startOfNextDay, endDate, [...days, startOfNextDay]);
    };
    return recursiveSplit(startDate, endDate, [moment(startDate).toDate()]);
  }

  static splitPeriodIntoHours(startDate, endDate) {
    const recursiveSplit = (startDate, endDate, hours = []) => {
      const startOfNextHour = moment(startDate)
        .add('1', 'hour')
        .startOf('hour')
        .toDate();
      if (startOfNextHour > moment(endDate).startOf('hour')) {
        return [...hours, moment(endDate).toDate()];
      }
      return recursiveSplit(startOfNextHour, endDate, [
        ...hours,
        startOfNextHour,
      ]);
    };
    return recursiveSplit(startDate, endDate, [moment(startDate).toDate()]);
  }

  static getPeriodGroupingType(startDate, endDate) {
    const dayDiff = moment(endDate).diff(moment(startDate), 'days') + 1;
    if (dayDiff === 1) {
      return this.GROUPING_TYPE_HOURS;
    }
    if (dayDiff <= 14) {
      return this.GROUPING_TYPE_DAYS;
    }
    if (dayDiff <= 90) {
      return this.GROUPING_TYPE_WEEKS;
    }
    return this.GROUPING_TYPE_MONTHS;
  }

  static splitPeriodIntoSeries(startDate, endDate) {
    const groupingType = this.getPeriodGroupingType(startDate, endDate);
    switch (groupingType) {
      case this.GROUPING_TYPE_HOURS:
        return this.splitPeriodIntoHours(startDate, endDate);
      case this.GROUPING_TYPE_DAYS:
        return this.splitPeriodIntoDays(startDate, endDate);
      case this.GROUPING_TYPE_WEEKS:
        return this.splitPeriodIntoWeeks(startDate, endDate);
      case this.GROUPING_TYPE_MONTHS:
        return this.splitPeriodIntoMonths(startDate, endDate);
      default:
        throw new Error(`Unknown grouping type ${groupingType}`);
    }
  }

  static isWorkingDay(day, workDays) {
    return workDays.includes(day);
  }

  static isWorkingHours(hour, startTime, endTime) {
    const castHour = Number(hour);
    return castHour >= startTime && (endTime === 0 || castHour < endTime);
  }

  static formatConversationPerHourStats(conversationPerHour, workingHours) {
    const data = this.getBaseData();

    conversationPerHour.forEach(({ day, hour, counter }) => {
      const record = data[Number(hour)];
      if (
        this.isWorkingDay(day.trim(), workingHours.workDays) &&
        this.isWorkingHours(hour, workingHours.startTime, workingHours.endTime)
      ) {
        record[1] = record[1] + Number(counter);
      } else {
        record[2] = record[2] + Number(counter);
      }
    });

    return data;
  }

  static getBaseData() {
    return [
      ['00', 0, 0],
      ['01', 0, 0],
      ['02', 0, 0],
      ['03', 0, 0],
      ['04', 0, 0],
      ['05', 0, 0],
      ['06', 0, 0],
      ['07', 0, 0],
      ['08', 0, 0],
      ['09', 0, 0],
      ['10', 0, 0],
      ['11', 0, 0],
      ['12', 0, 0],
      ['13', 0, 0],
      ['14', 0, 0],
      ['15', 0, 0],
      ['16', 0, 0],
      ['17', 0, 0],
      ['18', 0, 0],
      ['19', 0, 0],
      ['20', 0, 0],
      ['21', 0, 0],
      ['22', 0, 0],
      ['23', 0, 0],
    ];
  }
}
